/**
 * Who holds the callings the packet prints, and how to reach them.
 *
 * Built from Steward's own records rather than typed into a config file: the
 * bishopric changes, and a hand-kept copy of it in an emergency binder is
 * wrong in exactly the way that matters. Callings and phone numbers both come
 * from the LCR sync, so this block is right whenever the last sync was.
 *
 * A position nobody holds still prints — as the role with a rule beside it —
 * because a vacancy is information too, and the page should not silently
 * shrink when the second counselor is released.
 */
import { LEADERSHIP_POSITIONS, shortPosition } from '../incidentReference';
import type { LeadershipGroup } from './packet';
import type { SearchPeopleRow } from '../api';

export function leadershipFrom(people: SearchPeopleRow[]): LeadershipGroup[] {
  // Callings arrive as one comma-joined string per person, so the index is
  // built by splitting them back out. A person holding two of the printed
  // positions is listed under both, which is right — they answer for both.
  const byPosition = new Map<string, SearchPeopleRow>();
  for (const p of people) {
    for (const c of (p.callings ?? '').split(',')) {
      const key = c.trim().toLowerCase();
      if (key && !byPosition.has(key)) byPosition.set(key, p);
    }
  }
  return LEADERSHIP_POSITIONS.map(({ group, positions }) => ({
    group,
    rows: positions.map((position) => {
      const p = byPosition.get(position.toLowerCase());
      return {
        role: shortPosition(position),
        name: p?.name_full ?? '',
        phone: p?.phone ?? null,
      };
    }),
  }));
}

/** Everyone holding any open calling — one query, filtered in the browser. */
export const OPEN_CALLINGS_FILTER = {
  op: 'AND' as const,
  conditions: [{ field: 'has_open_calling', op: 'eq', value: true }],
};
